import { Injectable } from '@angular/core';
import { resolveInternalAppLink } from '@shared/helpers/functions/resolve-internal-app-link';

@Injectable({ providedIn: 'root' })
export class HTMLCleanerService {
  private filter: NodeFilter = {
    acceptNode: node => {
      if (node.nodeType === Node.COMMENT_NODE) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  };

  clear(htmlString: string): string {
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(htmlString, 'text/html');

    this.traverseNodesWithWalker(htmlDoc.body);
    this.ensureSafeAnchors(htmlDoc.body);

    return htmlDoc.body.innerHTML;
  }

  private traverseNodesWithWalker(rootNode: Node): void {
    const walker = document.createTreeWalker(
      rootNode,
      NodeFilter.SHOW_ALL,
      this.filter
    );

    let node: Node | null = walker.currentNode;

    while (node) {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = this.cleanPastedPlainText(node.textContent || '');
      }
      node = walker.nextNode();
    }
  }

  private cleanPastedPlainText(textNode: string): string {
    return textNode
      .normalize('NFC')
      .replace(/\v/g, '\n')
      .replace(/\p{Cf}/gu, '')
      .replace(/\u00AD/g, '')
      .replace(/\u202F/g, ' ')
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u2013\u2014]/g, '-');
  }

  private ensureSafeAnchors(rootNode: HTMLElement): void {
    const anchors = rootNode.querySelectorAll('a');
    anchors.forEach((anchor: HTMLAnchorElement) => {
      const internalRoute = resolveInternalAppLink(anchor.getAttribute('href'));

      if (internalRoute) {
        anchor.removeAttribute('target');
        // Prevent a new browsing context from inheriting this tab's auth
        // session while normal clicks continue to use same-tab SPA routing.
        anchor.rel = 'noopener';
        anchor.setAttribute('data-internal-link', internalRoute);
        return;
      }

      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
    });
  }
}
